import React, { useEffect, useRef, useState } from "react"
import { TrashIcon, MinusIcon, PlusIcon } from "@heroicons/react/24/outline"
import Img from "../Img"
import Chip from "../Chip"
import Button from "aldoo-ra/Button"
import PriceDisplay from "../PriceDisplay"
import useT from "../../hooks/useT"
import { buildContentUrl } from "../../utils/contentRoutes"
import { useCurrency } from "../../store/currency"
import { hasPlatformSelection, lineKey, lineProduct, lineUnitPrice, lineUnitPriceBefore, productCurrency, totalLicenses } from "../../checkout/cartItems"
import { activeDiscountPercent } from "../../utils/discountUtils"
import { licenseSummary } from "../../checkout/PaymentSummary"
import { openPlatformSelector } from "../../checkout/PlatformSelectorDialog"
import useSiteLanguage from "../../hooks/useSiteLanguage"
import { presaleOf, presaleDate } from "../../utils/presale"
import { overOrderLimit, orderLimitOf } from "../../utils/productUtils"
import { useCart, cartQuantityOf, updateCartLine } from "../../store/cart"
import useSingleCopy from "../../hooks/useSingleCopy"
import { scheduleStockCheck } from "../../store/stockCheck"

// A removal is not acted on at once: the line counts down first, and the bin
// turns into a way out of it. Three seconds is long enough to notice a
// misclick and short enough not to be in the way.
const REMOVE_DELAY = 2000

/**
 * The countdown of a line being removed, as a fraction from 0 to 1. It is
 * driven by animation frames rather than an interval, so the bar moves as
 * smoothly as the screen can draw it and stops while the tab is hidden.
 */
function useRemovalCountdown(onElapsed) {
  const [progress, setProgress] = useState(null)
  const frame = useRef(null)
  const elapsed = useRef(null)

  const stop = () => {
    if (frame.current) cancelAnimationFrame(frame.current)
    frame.current = null
    setProgress(null)
  }

  const start = () => {
    if (frame.current) return
    const startedAt = performance.now()
    const step = (now) => {
      const done = Math.min(1, (now - startedAt) / REMOVE_DELAY)
      elapsed.current = done
      setProgress(done)
      if (done < 1) {
        frame.current = requestAnimationFrame(step)
        return
      }
      frame.current = null
      onElapsed()
    }
    frame.current = requestAnimationFrame(step)
  }

  // a line that goes away mid-countdown must not keep a frame alive
  useEffect(() => () => frame.current && cancelAnimationFrame(frame.current), [])

  return { progress, pending: progress !== null, start, cancel: stop }
}

/** The buyer's choices of a line, as the product page labelled them ("Size: M · Color: Black") */
const choiceSummary = (line) => {
  const labels = line.choiceLabels && typeof line.choiceLabels === "object" ? Object.values(line.choiceLabels) : []
  const parts = labels.filter((entry) => entry?.value != null).map((entry) => (entry.label ? `${entry.label}: ${entry.value}` : `${entry.value}`))
  // an outfit's parts with what was picked on each ("T-shirt: M · Shorts: L")
  const components = (line.components || [])
    .map((part) => {
      const values = Object.values(part?.choiceLabels || {}).map((entry) => entry?.value).filter((value) => value != null && value !== "")
      return [part?.label, values.join(", ")].filter(Boolean).join(": ")
    })
    .filter(Boolean)
  return [...parts, ...components].join(" · ")
}

/**
 * Cart line (fragment "shopping-cart-product"): thumbnail, name, author,
 * unit price, quantity stepper — or the "N licenses" chip of a license
 * product, which reopens the platform selector, or nothing for what is had
 * once (a download) — and remove.
 * `issue` is the stock validation result of the line (CartFooter).
 */
export default function CartProduct({ product: line, store, issue }) {
  const t = useT()
  const product = lineProduct(line)
  // as many as one order may hold (the seller's limit), counted over every line of the product
  const cart = useCart()
  // the seller's limit, or the line's cap the server last answered (store/stockCheck)
  const capped = Number.isFinite(issue?.cap) && line.quantity >= issue.cap
  const overLimit = overOrderLimit(product, cartQuantityOf(cart, line.pid))
  const atLimit = overLimit || capped
  // why "+" stopped: the seller's limit, or the stock
  const limitText = overLimit && limit ? t("Up to %s of this item", limit) : capped ? t("Only %stock% available", issue.cap) : null
  const limit = orderLimitOf(product)
  const key = lineKey(line)
  const license = hasPlatformSelection(product)
  // had once (a download, access to content): no count to step, the bin removes it
  const single = useSingleCopy(product) && !license
  const licenses = totalLicenses(line)
  // the line is priced in the product's own currency; the cart shows it in
  // the one the cart is priced in
  const priced = productCurrency(product)
  const discount = activeDiscountPercent(product)
  const { currency, convert } = useCurrency()
  const invalid = !!issue && !issue.valid
  const removal = useRemovalCountdown(() => store.removeItem(key))
  const language = useSiteLanguage()
  const presale = presaleOf(product)

  const editLicenses = () =>
    openPlatformSelector({
      product,
      initialDistribution: line.options?.platform,
      initialQuantity: line.quantity,
      confirmLabel: t`Update`,
      // one change, so the quantity and the distribution land together
      onConfirm: ({ platform, licenses: total, universal }) =>
        store.setLine(key, { quantity: total, options: { platform, licenses: total, universal } }),
    })

  return (
    <div className={`flex gap-3 py-4 ${invalid ? "opacity-70" : ""}`} data-testid="cart-line" data-pid={line.pid}>
      {/* the picture alone: the price goes beside the quantity, the line stays short */}
      <div className="shrink-0 w-[72px]">
        <Button variant="none" href={buildContentUrl(product)} onClick={store.hideCart} className="shrink-0">
          <Img src={product.square || line.thumbnail} alt={line.name} width={72} height={72} className="w-[72px] h-[72px] rounded-md overflow-hidden" />
        </Button>
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <span className="text-[15px] font-semibold leading-tight line-clamp-2">{line.name}</span>
        {/* nothing to ship: a download, access to content or a licence — as the product page says it */}
        {(single || license) && <Chip tone="outline" size="sm" label={t`Digital Product`} className="w-fit uppercase tracking-wide text-[11px]" data-testid="cart-line-digital" />}
        {/* no author line: the cart keeps each line short */}
        {/* what the buyer chose on the product page: Size: M · Color: Black */}
        {choiceSummary(line) && <span className="text-[12px] text-text_secondary" data-testid="cart-line-choices">{choiceSummary(line)}</span>}
        {presale && (
          <span className="text-[12px] text-info font-semibold" data-testid="cart-line-presale">
            {presale.deliverBy ? t("Pre-sale · delivered by %date%", presaleDate(presale.deliverBy, language)) : t`Pre-sale`}
          </span>
        )}
        {license && licenseSummary(line, t) && <span className="text-[12px] text-text_secondary" data-testid="cart-line-platforms">{licenseSummary(line, t)}</span>}
        {invalid && <span className="text-[12px] text-error" data-testid="cart-line-issue">{issue.message}</span>}
        {!invalid && limitText && <span className="text-[12px] text-text_secondary" data-testid="cart-line-limit">{limitText}</span>}
        <div className="flex items-center gap-4 text-[14px] mt-1">
          {/* the price first, room before the quantity */}
          {/* the price paid; a discounted one as the cards show it: the -% label, the price in red, the old one struck */}
          {discount > 0 ? (
            <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0" data-testid="cart-line-price">
              <span className="inline-flex shrink-0 h-[22px] items-center rounded bg-primary_hov px-2 text-sm font-condensed font-medium text-white" data-testid="cart-line-discount">-{discount}%</span>
              <PriceDisplay amount={convert(lineUnitPrice(line), priced)} currency={currency} variant="small" className="min-w-0 text-primary" />
              <PriceDisplay amount={convert(lineUnitPriceBefore(line), priced)} currency={currency} variant="small_struck" struck className="opacity-50 self-start pt-[2px]" />
            </span>
          ) : (
            <PriceDisplay amount={convert(lineUnitPrice(line), priced)} currency={currency} variant="small" className="shrink-0 min-w-[72px]" data-testid="cart-line-price" />
          )}
          {single ? null : license ? (
            <button 
              type="button" 
              onClick={editLicenses} 
              className={`ml-auto inline-flex items-center h-7 px-3 rounded border text-[12px] font-semibold ${invalid ? "border-error text-error" : "border-primary text-primary"} hover:bg-primary/5`} 
              data-testid="cart-line-licenses"
            >
              {licenses} {licenses === 1 ? t`license` : t`licenses`}
            </button>
          ) : (
            /* the same pill as the side rail (CartStepper): at one, "−" is the bin — it removes, with
               its countdown, the pill a Cancel meanwhile */
            removal.pending ? (
              <button
                type="button"
                className="ml-auto inline-flex items-center justify-center w-[110px] h-[32px] rounded border border-text text-[13px] font-semibold hover:bg-text hover:text-paper"
                onClick={removal.cancel}
                data-testid="cart-line-remove-cancel"
              >
                {t`Cancel`}
              </button>
            ) : (
            <div className="ml-auto inline-flex items-center justify-between w-[110px] h-[32px] rounded border border-bordered bg-white text-text" data-testid="cart-line-stepper">
              {line.quantity <= 1 ? (
                <button type="button" className="flex items-center justify-center w-9 h-full text-text hover:text-error" onClick={removal.start} aria-label={t`Remove`} data-testid="cart-line-remove">
                  <TrashIcon className="w-4 h-4" />
                </button>
              ) : (
              <button 
                type="button" 
                className="flex items-center justify-center w-9 h-full text-text hover:text-primary" 
                onClick={() => { store.setQuantity(key, line.quantity - 1); scheduleStockCheck([key]) }} 
                aria-label={t`Less`}
              >
                <MinusIcon className="w-4 h-4 stroke-2" />
              </button>
              )}
              <span className="flex-1 text-center text-[14px] font-bold" data-testid="cart-line-quantity">
                {line.quantity}
              </span>
              <button 
                type="button" 
                className="flex items-center justify-center w-9 h-full text-text hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed" 
                disabled={atLimit} title={limitText || undefined} 
                onClick={() => { if (atLimit) return; updateCartLine(key, line.quantity + 1, line.options, "cart_line") }} 
                aria-label={t`More`}
              >
                <PlusIcon className="w-4 h-4 stroke-2" />
              </button>
            </div>
            )
          )}
          {/* a line with no quantity pill (a licence, a single digital copy): its own bin, its Cancel */}
          {(single || license) &&
            (removal.pending ? (
              <button
                type="button"
                className={`${license ? "" : "ml-auto "}inline-flex items-center h-7 px-3 rounded border border-text text-[12px] font-semibold hover:bg-text hover:text-paper`}
                onClick={removal.cancel}
                data-testid="cart-line-remove-cancel"
              >
                {t`Cancel`}
              </button>
            ) : (
              <button type="button" className={`${license ? "" : "ml-auto "}p-1 text-text_secondary hover:text-error`} onClick={removal.start} aria-label={t`Remove`} data-testid="cart-line-remove">
                <TrashIcon className="w-5 h-5" />
              </button>
            ))}
        </div>
        {/* the countdown of the removal, under the line it will take away */}
        {removal.pending && (
          <div className="mt-2 h-1 rounded-full bg-neutral_2 overflow-hidden" data-testid="cart-line-remove-progress">
            <div className="h-full bg-error" style={{ width: `${Math.round(removal.progress * 100)}%` }} />
          </div>
        )}
      </div>
    </div>
  )
}
